import { CheckCircleOutlined, FilePdfOutlined, MailOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, App as AntApp, Avatar, Button, Card, Descriptions, Empty, Input, Modal, Segmented, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BookingTotal, JobFlight, Segment } from '../../../src/types.ts';
import { api, errorText, type FlightBooking } from '../lib/api.ts';
import { fmtDateTime, fmtDay, fmtPrice, fmtTime } from '../lib/format.ts';
import { FlightItinerary } from '../components/FlightItinerary.tsx';

interface Props {
  email: string | null;
  /** Re-fetch trigger (after a new booking). */
  version: number;
  onWalletChanged: () => void;
}

type Raw = Record<string, unknown>;

interface Group {
  key: string;
  pnr: string | null;
  bookings: FlightBooking[];
  first: FlightBooking;
  segments: Segment[];
  route: string;
  departs: string | null;
  isRoundTrip: boolean;
  total: number;
  currency: string | null;
  status: string;
  expiresAt: string | null;
}

const STATUS: Record<string, { label: string; color: string }> = {
  INITIAL: { label: 'On hold', color: 'orange' },
  INITIAL_BOOKING: { label: 'On hold', color: 'orange' },
  RESERVE_FARE: { label: 'Fare reserved', color: 'orange' },
  FINAL_BOOKING: { label: 'Ticketed', color: 'green' },
  PNR_ONLY: { label: 'PNR only', color: 'blue' },
  ONEWAY_USED: { label: 'Outbound flown', color: 'blue' },
  TWO_WAY_USED: { label: 'Completed', color: 'default' },
  EXPIRED: { label: 'Expired', color: 'red' },
  CANCELLED_FULL_REFUND: { label: 'Cancelled · refunded', color: 'red' },
  CANCELLED_PENALTY_REFUND: { label: 'Cancelled · penalty', color: 'red' },
  CANCEL_REQUEST: { label: 'Cancel requested', color: 'volcano' },
  CHANGE_REQUEST: { label: 'Change requested', color: 'volcano' },
  HISTORY: { label: 'History', color: 'default' },
  NEW: { label: 'New', color: 'blue' },
};

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : typeof v === 'number' ? String(v) : null);
const name = (v: unknown): string | null => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Raw;
    return str(o.nameEn) ?? str(o.name) ?? null;
  }
  return null;
};

/** A booking's itinerary rows, in the shape the itinerary view already understands. */
function toSegments(itineraries: Raw[] | undefined): Segment[] {
  return (itineraries ?? [])
    .slice()
    .sort((a, b) => Number(a.flightOrder ?? a.segment ?? 0) - Number(b.flightOrder ?? b.segment ?? 0))
    .map((it) => {
      const dep = (it.departureAirport ?? {}) as Raw;
      const arr = (it.arrivalAirport ?? {}) as Raw;
      const al = (it.airline ?? {}) as Raw;
      return {
        flightNo: str(it.flightNo),
        dep: str(it.departureDateTime),
        arr: str(it.arrivalDateTime),
        duration: str(it.duration),
        airline: name(al) ?? str(it.airlineName),
        airlineCode: str(al.iataCode),
        airlineLogo: str(al.imageUrl),
        from: str(dep.iataCode),
        to: str(arr.iataCode),
        fromCity: name(dep.city),
        toCity: name(arr.city),
        fromName: name(dep.name),
        toName: name(arr.name),
        cabinClass: str(it.cabinClass),
        ticketClass: str(it.ticketClass),
        bagsText: str(it.bags)?.replace(/\s+/g, ' ') ?? null,
      };
    });
}

/** etravel stores one record per passenger; a trip is the set sharing a PNR. */
function groupBookings(list: FlightBooking[]): Group[] {
  const by = new Map<string, FlightBooking[]>();
  for (const b of list) {
    const key = b.pnr || b.etravelReference || `id-${b.id}`;
    const arr = by.get(key);
    if (arr) arr.push(b);
    else by.set(key, [b]);
  }
  return [...by.entries()].map(([key, bookings]) => {
    const first = bookings[0]!;
    const segments = toSegments(first.itineraries as Raw[] | undefined);
    const from = segments[0]?.from ?? '?';
    const isRoundTrip = /RETURN/i.test(String(first.flightType ?? '')) || (segments.length > 1 && segments[segments.length - 1]?.to === from);
    const dest = isRoundTrip ? segments[Math.ceil(segments.length / 2) - 1]?.to : segments[segments.length - 1]?.to;
    const total = bookings.reduce((s, b) => s + Number(b.paxTotal ?? b.total ?? 0), 0);
    return {
      key,
      pnr: first.pnr ?? null,
      bookings,
      first,
      segments,
      route: `${from} ${isRoundTrip ? '⇆' : '→'} ${dest ?? '?'}`,
      departs: segments[0]?.dep ?? null,
      isRoundTrip,
      total,
      currency: first.currency ?? null,
      status: String(first.bookingType ?? ''),
      expiresAt: first.initialBookingExpiration ?? null,
    };
  });
}

type Filter = 'all' | 'upcoming' | 'held' | 'past' | 'cancelled';

const feeLabel = (f: Raw) => String(f.descriptionEn || f.description || f.name || 'Fee');

/** Every trip booked on this account, with the actions etravel allows on each. */
export function BookingsPage({ email, version, onWalletChanged }: Props) {
  const { message, modal } = AntApp.useApp();
  const [list, setList] = useState<FlightBooking[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [emailFor, setEmailFor] = useState<Group | null>(null);
  const [emailTo, setEmailTo] = useState(email ?? '');

  const load = useCallback(() => {
    setError(null);
    api
      .bookings()
      .then((r) => {
        setList(r.items);
        setTotal(r.total);
      })
      .catch((e) => setError(errorText(e)));
  }, []);
  useEffect(load, [load, version]);
  useEffect(() => setEmailTo(email ?? ''), [email]);

  const groups = useMemo(() => groupBookings(list ?? []), [list]);
  const now = Date.now();
  const shown = groups.filter((g) => {
    const dep = g.departs ? Date.parse(g.departs) : NaN;
    const cancelled = /CANCEL|EXPIRED/.test(g.status);
    const held = /INITIAL|RESERVE/.test(g.status);
    if (filter === 'cancelled') return cancelled;
    if (filter === 'held') return held;
    if (filter === 'upcoming') return !cancelled && Number.isFinite(dep) && dep >= now - 6 * 3_600_000;
    if (filter === 'past') return !cancelled && Number.isFinite(dep) && dep < now - 6 * 3_600_000;
    return true;
  });

  const showQuote = (title: string, q: BookingTotal | Raw, okText: string, onOk: () => Promise<void>) => {
    const fees = Array.isArray((q as BookingTotal).fees) ? ((q as BookingTotal).fees as Raw[]) : [];
    const totalAmt = (q as BookingTotal).total;
    const currency = (q as BookingTotal).currency;
    const extras = Object.entries(q).filter(([k, v]) => !['fees', 'total', 'currency', 'preBookId'].includes(k) && (typeof v === 'number' || typeof v === 'string'));
    modal.confirm({
      title,
      okText,
      width: 520,
      content: (
        <Descriptions
          size="small"
          bordered
          column={1}
          style={{ marginTop: 12 }}
          items={[
            ...fees.map((f, i) => ({ key: `f${i}`, label: feeLabel(f), children: fmtPrice(Number(f.amount), str(f.currency) ?? currency) })),
            ...extras.map(([k, v]) => ({ key: k, label: k.replace(/([A-Z])/g, ' $1').toLowerCase(), children: String(v) })),
            ...(totalAmt != null ? [{ key: 't', label: <strong>Total</strong>, children: <strong>{fmtPrice(Number(totalAmt), currency)}</strong> }] : []),
          ]}
        />
      ),
      onOk,
    });
  };

  const confirmHold = async (g: Group) => {
    setBusy(g.key);
    try {
      const q = await api.confirmInitialDetails(g.first.id);
      showQuote(`Confirm and pay for ${g.route}`, q, 'Pay from wallet', async () => {
        await api.confirmInitial(g.first.id);
        message.success('Booking confirmed and ticketed.');
        onWalletChanged();
        load();
      });
    } catch (err) {
      message.error(errorText(err), 8);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (g: Group) => {
    setBusy(g.key);
    try {
      const q = await api.cancelDetails(g.first.id);
      showQuote(`Cancel ${g.route}?`, q, 'Cancel booking', async () => {
        await api.cancelBooking(g.first.id);
        message.success('Booking cancelled. Any refund goes back to the wallet.');
        onWalletChanged();
        load();
      });
    } catch (err) {
      message.error(errorText(err), 8);
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async () => {
    if (!emailFor?.first.etravelReference) return;
    setBusy(emailFor.key);
    try {
      await api.sendTicketEmail(emailFor.first.etravelReference, emailTo.trim());
      message.success(`Ticket sent to ${emailTo.trim()}.`);
      setEmailFor(null);
    } catch (err) {
      message.error(errorText(err), 8);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page">
      <Card
        size="small"
        title={
          <Space>
            <span>My bookings</span>
            {total != null && <Tag bordered={false}>{total}</Tag>}
          </Space>
        }
        extra={
          <Space wrap>
            <Segmented
              size="small"
              value={filter}
              onChange={(v) => setFilter(v as Filter)}
              options={[
                { label: 'All', value: 'all' },
                { label: 'Upcoming', value: 'upcoming' },
                { label: 'On hold', value: 'held' },
                { label: 'Past', value: 'past' },
                { label: 'Cancelled', value: 'cancelled' },
              ]}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={load} />
          </Space>
        }
      >
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        {list === null && !error && <Spin />}
        {list && shown.length === 0 && <Empty description={groups.length ? 'Nothing in this view.' : 'No bookings yet. Find a fare in Search and book it from the day view.'} />}
        <div className="bookings">
          {shown.map((g) => {
            const st = STATUS[g.status] ?? { label: g.status.replace(/_/g, ' ').toLowerCase() || 'booking', color: 'default' };
            const held = /INITIAL|RESERVE/.test(g.status);
            const cancellable = /FINAL_BOOKING|ONEWAY_USED|PNR_ONLY/.test(g.status);
            const open = openKey === g.key;
            const fake: JobFlight = {
              provider: g.first.gdsType ? String(g.first.gdsType) : 'etravel',
              gdsType: String(g.first.gdsType ?? ''),
              price: g.total,
              currency: g.currency ?? 'LYD',
              airline: g.segments[0]?.airline ?? '',
              airlineCode: g.segments[0]?.airlineCode ?? null,
              airlineLogo: g.segments[0]?.airlineLogo ?? null,
              flightNo: g.segments[0]?.flightNo ?? null,
              departTime: g.departs,
              arriveTime: g.segments[g.segments.length - 1]?.arr ?? null,
              duration: null,
              stops: 0,
              flightType: g.isRoundTrip ? 'STOPS_RETURN' : 'STOPS_ONEWAY',
              refundable: null,
              segments: g.segments,
              originCode: g.segments[0]?.from ?? '',
              destinationCode: g.isRoundTrip ? (g.segments[Math.ceil(g.segments.length / 2) - 1]?.to ?? '') : (g.segments[g.segments.length - 1]?.to ?? ''),
              returnDate: g.isRoundTrip ? (g.segments[Math.ceil(g.segments.length / 2)]?.dep?.slice(0, 10) ?? null) : null,
            };
            return (
              <div key={g.key} className={`booking${open ? ' booking--open' : ''}`}>
                <div className="booking__head" onClick={() => setOpenKey(open ? null : g.key)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenKey(open ? null : g.key)}>
                  <Avatar size={36} shape="square" src={g.segments[0]?.airlineLogo ?? undefined}>
                    {(g.segments[0]?.airlineCode ?? '?').slice(0, 2)}
                  </Avatar>
                  <div className="booking__main">
                    <div className="booking__route">
                      {g.route}
                      <Tag color={st.color} bordered={false} style={{ marginInlineStart: 8 }}>
                        {st.label}
                      </Tag>
                    </div>
                    <div className="booking__meta">
                      {g.departs ? `${fmtDay(g.departs)} ${fmtTime(g.departs)}` : ''}
                      {g.segments.length ? ` · ${g.segments.map((s) => s.flightNo).filter(Boolean).join(', ')}` : ''}
                      {' · '}
                      {g.bookings.length} passenger{g.bookings.length === 1 ? '' : 's'}
                      {g.pnr ? (
                        <>
                          {' · PNR '}
                          <Typography.Text strong copyable={{ text: g.pnr }}>
                            {g.pnr}
                          </Typography.Text>
                        </>
                      ) : null}
                    </div>
                    {held && g.expiresAt && (
                      <div className="booking__expiry">
                        Hold expires {fmtDateTime(g.expiresAt)}
                        {Date.parse(g.expiresAt) < now ? ' — passed' : ''}
                      </div>
                    )}
                  </div>
                  <div className="booking__price">{fmtPrice(g.total, g.currency)}</div>
                </div>
                {open && (
                  <div className="booking__body">
                    <FlightItinerary flight={fake} compact />
                    <div className="booking__pax">
                      {g.bookings.map((b) => (
                        <div key={b.id} className="booking__paxrow">
                          <span className="booking__paxname">
                            {b.passengerFirstName} {b.passengerLastName}
                            {b.paxType ? <Tag bordered={false} style={{ marginInlineStart: 6 }}>{String(b.paxType)}</Tag> : null}
                          </span>
                          <span className="booking__paxmeta">
                            {b.passengerPassportNo ? `Passport ${b.passengerPassportNo}` : ''}
                            {b.eTicketNo ? ` · e-ticket ${b.eTicketNo}` : ' · no e-ticket yet'}
                            {b.etravelReference ? ` · ref ${b.etravelReference}` : ''}
                          </span>
                          <span className="booking__paxprice">{fmtPrice(b.paxTotal ?? b.total ?? null, b.currency)}</span>
                          {b.etravelReference && (
                            <Tooltip title="Invoice / ticket PDF">
                              <Button size="small" type="text" icon={<FilePdfOutlined />} href={api.invoiceUrl(b.etravelReference)} target="_blank" rel="noreferrer" />
                            </Tooltip>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="booking__actions">
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {[g.first.createdDate ? `Booked ${fmtDateTime(g.first.createdDate)}` : '', g.first.issuedAt ? `Issued ${fmtDateTime(g.first.issuedAt)}` : '']
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography.Text>
                      <Space wrap>
                        {g.first.etravelReference && (
                          <Button size="small" icon={<MailOutlined />} onClick={() => setEmailFor(g)}>
                            Email ticket
                          </Button>
                        )}
                        {held && (
                          <Button size="small" type="primary" icon={<CheckCircleOutlined />} loading={busy === g.key} onClick={() => void confirmHold(g)}>
                            Confirm & pay
                          </Button>
                        )}
                        {cancellable && (
                          <Button size="small" danger icon={<StopOutlined />} loading={busy === g.key} onClick={() => void cancel(g)}>
                            Cancel booking
                          </Button>
                        )}
                      </Space>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
      <Modal open={emailFor !== null} title="Email the ticket" onCancel={() => setEmailFor(null)} onOk={() => void sendEmail()} okText="Send" confirmLoading={busy === emailFor?.key}>
        <Typography.Paragraph type="secondary">{emailFor?.route}</Typography.Paragraph>
        <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="name@example.com" />
      </Modal>
    </div>
  );
}
